import React from 'react'
import PropTypes from 'prop-types'

import { annuity } from '@utils/financeCalculations'
import { R } from '@utils/currency'
import { TimeValue } from '@hooks/useTimeValue'
import { Card, CardHeading, CardContent } from '@components/layout/Card'
import './safe-retirement-indicator.scss'

const WIDTH = 300
const HEIGHT = 0
const SCALE_FACTOR = WIDTH / 100

const Root = (props) => {
  return (
    <Card className='safe-retirement-indicator'>
      {props.children}
    </Card>
  )
}

const Heading = ({ children }) => (
  <CardHeading>
    {children ?? <h5>Safe Retirement Status</h5>}
  </CardHeading>
)

const Indicator = ({ safeRetirementStatus }) => {
  const { text, percentage } = safeRetirementStatus

  return (
        <CardContent>
          <SafeRetirementStatus text={text} />
          <SafeRetirementBar percentage={percentage} />
        </CardContent>
  )
}

const CapitalRequirementsCardExtension = ({ headingText, capitalProps }) => {
  const {
    capitalForPossibly, capitalForLikely, capitalAtRetirement,
    inflation, yearsToRetirement, timeValue
  } = capitalProps

  const values = formattedAndAdjustedToTimeValue(
    { capitalForPossibly, capitalForLikely, capitalAtRetirement },
    inflation, yearsToRetirement, timeValue
  )
  const [visible, setVisible] = React.useState(false)

  return (
    <>
      <CardHeading className={`safe-retirement-indicator__nested-heading ${visible && 'visible'}`}>
        <h5>{headingText ?? 'Capital requirements' }</h5>
        <span onClick={() => setVisible(!visible)}>{visible ? 'Hide' : 'Show'}</span>
      </CardHeading>
      <CardContent>
        <CapitalRequirements
          capitalAtRetirement={values.capitalAtRetirement}
          capitalForPossiblyLabel={values.capitalForPossibly}
          capitalForLikelyLabel={values.capitalForLikely} />
      </CardContent>
    </>
  )
}

const formattedAndAdjustedToTimeValue = (objectWithNamedValues, inflation, yearsToRetirement, timeValue) => {
  return Object.keys(objectWithNamedValues).reduce((acc, name) => {
    const value = objectWithNamedValues[name]
    const convertedValue = timeValue === TimeValue.present
      ? annuity(value, -yearsToRetirement, inflation)
      : value
    const formattedValue = R(convertedValue).format()

    return { ...acc, [name]: formattedValue }
  }, {})
}

const SafeRetirementStatus = ({ text }) => (
  <label className='safe-retirement-indicator__status'>{text}</label>
)

const SafeRetirementBar = ({ percentage }) => {
  const viewBox = `0 0 ${WIDTH} 30`

  return (
    <div>
      <div style={{ position: 'relative', height: '35px', width: '100%', padding: 0, paddingBottom: `${100 * (HEIGHT / WIDTH)}%` }}>
        <svg viewBox={viewBox} xmlns='http://www.w3.org/2000/svg' style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0 }}>
          <defs>
            <linearGradient id='retirement-gradient'>
              <stop offset='0%' stopColor='#BF616A' />
              <stop offset='50%' stopColor='#EBCB8B' />
              <stop offset='100%' stopColor='#A3CE8C' />
            </linearGradient>
          </defs>
          <g fill="url('#retirement-gradient')">
            <path d={barSvgPath(percentage)} strokeLinecap='round' />
          </g>
        </svg>
      </div>
    </div>
  )
}

const barSvgPath = (percentage) => {
  let circleCenter
  if (percentage < 15 / SCALE_FACTOR) {
    circleCenter = 15
  } else if (percentage > 100 - 15 / SCALE_FACTOR) {
    circleCenter = WIDTH - 15
  } else {
    circleCenter = Math.floor(percentage * SCALE_FACTOR)
  }
  circleCenter = isNaN(circleCenter) ? 0 : circleCenter

  return `M 0 10 L ${circleCenter - 10} 10 C ${circleCenter - 5} 2 ${circleCenter + 5} 2 ${circleCenter + 10} 10 L ${WIDTH} 10 L ${WIDTH} 20 L ${circleCenter + 10} 20 C ${circleCenter + 5} 28 ${circleCenter - 5} 28 ${circleCenter - 10} 20 L 0 20`
}

const CapitalRequirements = ({
  capitalForPossiblyLabel, capitalForLikelyLabel, capitalAtRetirement
}) => {
  return (
    <div className='safe-retirement-indicator__capital-requirements'>
      <label><span>At retirement</span><span>{capitalAtRetirement}</span></label>
      <label><span>For 'Possibly'</span><span>{capitalForPossiblyLabel}</span></label>
      <label><span>For 'Likely'</span><span>{capitalForLikelyLabel}</span></label>
    </div>
  )
}

export const SafeRetirementIndicator = Object.assign(Root, {
  Heading,
  Indicator,
  CapitalRequirementsCardExtension
})

Indicator.propTypes = {
  safeRetirementStatus: PropTypes.shape({
    text: PropTypes.string.isRequired,
    percentage: PropTypes.number.isRequired,
    includeHeading: PropTypes.bool.isRequired,
  }),
}

CapitalRequirementsCardExtension.propTypes = {
  headingText: PropTypes.string,
  capitalProps: PropTypes.shape({
    capitalForPossibly: PropTypes.number.isRequired,
    capitalForLikely: PropTypes.number.isRequired,
    capitalAtRetirement: PropTypes.number.isRequired,
    inflation: PropTypes.number.isRequired,
    yearsToRetirement: PropTypes.number.isRequired,
    timeValue: PropTypes.oneOf([TimeValue.present, TimeValue.future]).isRequired
  })
}
